import Cookies from 'js-cookie';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as events from '../lib/analytics/events';
import { logInternalError } from '../lib/errors';
import getCouponCookie from '../lib/getCouponCookie';
import getReferralCookie from '../lib/getReferralCookie';
import {
  HARDWARE_FIRST_IN_APP_PROMO_SOURCE,
  shouldAutoApplyHardwareFirstInAppPromo,
} from '../lib/hardwareFirstInAppPromo';
import { inPartnerPromoContext } from '../lib/hardwareFirstPartnerContext';
import { applyCouponToCart } from '../lib/promoCode';
import { cartActions } from '../reducers/storeCart';
import * as types from '../types';
import { giftCardCookieName } from '../types/Product';
import { useActivePromoCode } from './useActivePromoCode';

/**
 * Applies the live promotion's code to the in-app hardware-first store cart, so a member gets the
 * banner price without typing the code the banner tells web shoppers to use. The server prices the
 * hardware from the code exactly as it does for a typed one. Mount only for an in-app session in the
 * arm: `useActivePromoCode` reaches `usePromotion`, which reports a banner impression on first fetch.
 */
export default function useApplyHardwareFirstInAppPromo(): void {
  const dispatch = useDispatch();
  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const activePromoCode = useActivePromoCode();
  // keyed on the cart's contents: a new item is worth a fresh attempt, a re-render is not
  const cartSkus = Object.values(cart.cartItems)
    .map((cartItem) => cartItem.lineItem.sku)
    .sort()
    .join(',');
  const attemptKey = activePromoCode ? `${activePromoCode}:${cartSkus}` : undefined;
  const attempted = useRef(new Set<string>());
  // read inside the async callback, so it must reflect the cart at response time, not at request time
  const cartHasCode = !!(cart.couponCode || cart.referralCode || cart.redeemedGiftCardCode);
  const cartHasCodeRef = useRef(cartHasCode);
  cartHasCodeRef.current = cartHasCode;

  useEffect(() => {
    if (
      !activePromoCode ||
      !attemptKey ||
      !shouldAutoApplyHardwareFirstInAppPromo({
        activePromoCode,
        cartItemCount: Object.keys(cart.cartItems).length,
        cartCouponCode: cart.couponCode,
        cartReferralCode: cart.referralCode,
        redeemedGiftCardCode: cart.redeemedGiftCardCode,
        hasCouponCookie: !!getCouponCookie(),
        hasReferralCookie: !!getReferralCookie(),
        hasGiftCardCookie: !!Cookies.get(giftCardCookieName),
        inPartnerContext: inPartnerPromoContext(),
        alreadyAttempted: attempted.current.has(attemptKey),
      })
    ) {
      return;
    }
    // recorded before the await so a re-render mid-flight cannot price the cart twice
    attempted.current.add(attemptKey);

    applyCouponToCart(cart, activePromoCode)
      .then((result) => {
        // a code the server rejects for this cart resolves as a failure value and is not retried. any
        // coupon, referral or gift card that landed while the request was in flight wins over this one
        if (result.kind === 'success' && !cartHasCodeRef.current) {
          dispatch(cartActions.addCoupon(activePromoCode));
          events.cartPage.couponAutoApplied(activePromoCode, HARDWARE_FIRST_IN_APP_PROMO_SOURCE);
        }
      })
      .catch((err) => logInternalError(err, { tags: { context: 'useApplyHardwareFirstInAppPromo' } }));
  }, [activePromoCode, attemptKey, cart, dispatch]);
}
